import { retryAfterMs, retryDelayWithJitter } from './request-cooldown.service';
import { computed, Injectable, OnDestroy, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { config } from '../../../environments/environment';
import { tenantHeaders } from '../../shared/helpers/tenant';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';

import { MaintenanceStatusSchema, isMaintenancePhase } from '@andor/contracts/maintenance';
import type { MaintenanceStatus } from '@andor/contracts/maintenance';
export { isMaintenancePhase };
export type { MaintenanceStatus };
export interface BackendRecovery {
  generation: number;
  signal: AbortSignal;
}

/** Availability starts recovery; only its coordinator can reopen application work. */
@Injectable({ providedIn: 'root' })
export class MaintenanceService implements OnDestroy {
  readonly status = signal<MaintenanceStatus | null>(null);
  readonly recovering = signal(false);
  readonly throttled = signal(false);
  readonly active = computed(() => this.status()?.available === false || this.recovering());
  readonly confirmed = computed(() => isMaintenancePhase(this.status()?.phase ?? ''));
  readonly requiresAdminAction = computed(
    () =>
      this.status()?.available === false &&
      !this.confirmed() &&
      this.status()?.requiresAdminAction === true
  );
  readonly noticeVisible = signal(false);
  readonly title = computed(() =>
    this.confirmed()
      ? _('Maintenance')
      : this.requiresAdminAction()
        ? _('Waiting for administrator')
        : this.throttled()
          ? _('Waiting to retry')
          : _('Reconnecting')
  );
  readonly message = computed(() =>
    this.confirmed()
      ? (this.status()?.message ?? '')
      : this.requiresAdminAction()
        ? _('An administrator needs to take action before the service is available.')
        : this.throttled()
          ? _('The server requested a short wait before reconnecting.')
          : _('Reconnecting to the service.')
  );
  readonly changes = new Subject<MaintenanceStatus>();
  readonly entered = new Subject<void>();
  readonly resumed = new Subject<void>();
  readonly recoveryRequested = new Subject<BackendRecovery>();
  private pending?: Promise<boolean>;
  private timer?: ReturnType<typeof setTimeout>;
  private noticeTimer?: ReturnType<typeof setTimeout>;
  private recoveryTimer?: ReturnType<typeof setTimeout>;
  private recoveryAbort?: AbortController;
  private generation = 0;
  private observation = 0;
  private failures = 0;
  private destroyed = false;
  private probeAbort?: AbortController;
  get revision() {
    return this.observation;
  }

  accept(input: unknown): boolean {
    if (this.destroyed) return false;
    const parsed = MaintenanceStatusSchema.safeParse(input);
    if (!parsed.success) return false;
    const s = parsed.data;
    const confirmed = isMaintenancePhase(s.phase);
    const previous = this.status();
    if (
      previous?.controlVersion &&
      (!s.controlVersion || s.controlVersion < previous.controlVersion)
    )
      return false;
    const wasActive = this.active();
    this.observation++;
    // Legacy normal responses can still carry the previous operation/message.
    const status = {
      ...s,
      operation: confirmed ? s.operation : null,
      message: confirmed ? s.message : '',
    };
    this.status.set(status);
    clearTimeout(this.timer);
    if (!status.available) {
      if (!wasActive || this.recovering()) {
        this.cancelRecovery();
        this.entered.next();
      }
      if (confirmed || this.requiresAdminAction()) this.noticeVisible.set(true);
      else if (!this.noticeTimer && !this.noticeVisible()) {
        this.noticeTimer = setTimeout(() => this.noticeVisible.set(this.active()), 5000);
      }
      this.scheduleProbe();
    } else if (wasActive && !this.recovering()) {
      this.beginRecovery();
    }
    this.changes.next(status);
    return true;
  }

  private beginRecovery() {
    this.throttled.set(false);
    this.recovering.set(true);
    const generation = ++this.generation;
    const abort = (this.recoveryAbort = new AbortController());
    this.recoveryTimer = setTimeout(() => this.failRecovery(generation), 15_000);
    queueMicrotask(() => {
      if (this.isRecoveryCurrent(generation))
        this.recoveryRequested.next({ generation, signal: abort.signal });
    });
  }

  isRecoveryCurrent(generation: number | null | undefined): boolean {
    return (
      generation === this.generation &&
      this.recovering() &&
      this.status()?.available === true &&
      !this.recoveryAbort?.signal.aborted
    );
  }
  completeRecovery(generation: number): boolean {
    if (!this.isRecoveryCurrent(generation)) return false;
    this.observation++;
    clearTimeout(this.recoveryTimer);
    clearTimeout(this.noticeTimer);
    this.noticeTimer = undefined;
    this.recoveryAbort = undefined;
    this.recovering.set(false);
    this.noticeVisible.set(false);
    this.failures = 0;
    this.throttled.set(false);
    this.resumed.next();
    return true;
  }
  failRecovery(generation: number, error?: unknown) {
    if (!this.isRecoveryCurrent(generation)) return;
    const delay = retryAfterMs(error);
    if (delay !== null) {
      // Waiting does not spend the 15-second execution allowance. Do not mark
      // this as maintenance or discard the current meeting's identity/media.
      this.cancelRecovery();
      this.throttled.set(true);
      this.recovering.set(true);
      this.noticeVisible.set(true);
      this.recoveryTimer = setTimeout(() => this.beginRecovery(), retryDelayWithJitter(delay));
    } else this.markUnavailable();
  }
  markUnavailable(revision = this.observation) {
    if (revision !== this.observation) return;
    const previous = this.status();
    this.accept(
      (this.confirmed() || this.requiresAdminAction()) && previous
        ? { ...previous, available: false }
        : {
            available: false,
            phase: 'unavailable',
            operation: null,
            message: '',
            controlVersion: previous?.controlVersion,
          }
    );
  }
  private cancelRecovery() {
    this.throttled.set(false);
    this.generation++;
    this.recoveryAbort?.abort();
    this.recoveryAbort = undefined;
    clearTimeout(this.recoveryTimer);
    this.recovering.set(false);
  }
  private scheduleProbe() {
    const delay =
      this.confirmed() || this.requiresAdminAction()
        ? 30_000
        : Math.min(1000 * 2 ** Math.min(this.failures++, 5), 30_000);
    this.timer = setTimeout(() => void this.check(), delay * (0.9 + Math.random() * 0.2));
  }
  check(): Promise<boolean> {
    this.pending ??= this.probe().finally(() => {
      this.pending = undefined;
    });
    return this.pending;
  }
  private async probe() {
    const observation = this.observation;
    const abort = (this.probeAbort = new AbortController());
    const timeout = setTimeout(() => abort.abort(), 5000);
    try {
      const response = await fetch(`${config.backendUrl}/v1/maintenance`, {
        cache: 'no-store',
        headers: tenantHeaders(),
        signal: abort.signal,
      });
      if (!response.ok) throw new Error('Status unavailable');
      const status: unknown = await response.json();
      if (observation === this.observation && !this.accept(status)) {
        if (this.active()) this.scheduleProbe();
        else this.markUnavailable(observation);
      }
    } catch {
      this.markUnavailable(observation);
    } finally {
      clearTimeout(timeout);
    }
    return this.status()?.available === true;
  }
  ngOnDestroy() {
    this.destroyed = true;
    this.observation++;
    this.probeAbort?.abort();
    clearTimeout(this.timer);
    clearTimeout(this.noticeTimer);
    this.cancelRecovery();
  }
}
